import { useState } from 'react';
import { Braces } from 'lucide-react';
import {
  Button,
  InShellPanel,
  InShellPanelDescription,
  InShellPanelHeader,
  InShellPanelTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@staff-app/ui';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';
import { DeviceCapabilityDiagnostic } from '@/features/customers/components/DeviceCapabilityDiagnostic';
import { DeviceHealthStrip } from './DeviceHealthStrip';
import { DeviceInfoList } from './DeviceInfoList';
import { DeviceJsonDialog } from './DeviceJsonDialog';
import { DeviceStatusDot } from './DeviceStatusDot';

/**
 * Staff variant of the MDM device detail drawer.
 *
 * Deltas vs apps/web copy:
 *  - No `MdmButtonRow` / "Commands" card: staff are cross-tenant viewers in
 *    Phase 1, not command executors on arbitrary customers' devices.
 *  - Resolves the device from the list already loaded by `useStaffCustomerDevices`
 *    rather than a second `getDeviceDetail` round-trip — there is no per-device
 *    staff query, and the list projection already includes every field we need.
 *  - Otherwise identical layout + components + drawer width.
 *
 * Keep this list of deltas in sync with:
 *   scio3/docs/specs/19-optiedge-mdm/staff-devices-sync.md
 */
interface Props {
  devices: MdmDevice[];
  deviceId: string | null;
  onClose: () => void;
}

export function DeviceDetailDrawer({ devices, deviceId, onClose }: Props) {
  const [jsonOpen, setJsonOpen] = useState(false);

  const device = deviceId
    ? devices.find((d) => d._id === deviceId || d.UUID === deviceId)
    : undefined;
  const isOpen = !!deviceId;
  // Staff drawer reads from the list prop — so "device missing" means the
  // id didn't match any row (different team / stale deep-link). No async load
  // here, so skip the loading state and go straight to not-found.
  const notFound = !!deviceId && !device;

  return (
    <InShellPanel
      open={isOpen}
      onOpenChange={(next) => !next && onClose()}
      side="right"
      data-testid="mdm-device-detail-drawer"
      closeLabel={'Close'}
    >
      <InShellPanelHeader className="mb-4">
        <InShellPanelTitle className="flex items-center gap-2">
          {device && <DeviceStatusDot device={device} />}
          <span className="truncate">
            {device?.deviceName ?? (notFound ? 'Device unavailable' : 'Loading...')}
          </span>
        </InShellPanelTitle>
        <InShellPanelDescription className="sr-only">
          {'Device detail panel'}
        </InShellPanelDescription>
      </InShellPanelHeader>

      {device ? (
        <Tabs defaultValue="overview" className="flex flex-col gap-4">
          <TabsList className="self-start">
            <TabsTrigger value="overview" className="cursor-pointer">
              {'Overview'}
            </TabsTrigger>
            <TabsTrigger value="apps" className="cursor-pointer" disabled>
              {'Apps'}
            </TabsTrigger>
            <TabsTrigger value="logs" className="cursor-pointer" disabled>
              {'Logs'}
            </TabsTrigger>
            <TabsTrigger value="events" className="cursor-pointer" disabled>
              {'Events'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <DeviceHealthStrip device={device} />

            <div className="rounded-lg border bg-card p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-muted-foreground">
                  {'Device info'}
                </span>
                <Button
                  variant="outline"
                  size="icon-md"
                  aria-label={'Show raw JSON'}
                  title={'Show raw JSON'}
                  onClick={() => setJsonOpen(true)}
                  data-testid="mdm-device-json-button"
                >
                  <Braces className="h-4 w-4" />
                </Button>
              </div>
              <DeviceInfoList device={device} />
            </div>

            {/* Phase 7 T074 — 3-column capability diagnostic. Uses the
                staff-only `matrixCapabilities` resolver. Spec §6.3. */}
            <DeviceCapabilityDiagnostic device={device} />
          </TabsContent>

          <TabsContent value="apps">
            <div className="text-sm text-muted-foreground">{'Coming soon'}</div>
          </TabsContent>

          <TabsContent value="logs">
            <div className="text-sm text-muted-foreground">{'Coming soon'}</div>
          </TabsContent>

          <TabsContent value="events">
            <div className="text-sm text-muted-foreground">{'Coming soon'}</div>
          </TabsContent>
        </Tabs>
      ) : notFound ? (
        <div className="text-sm text-muted-foreground" data-testid="mdm-device-detail-not-found">
          {"This device isn't in the current list. Refresh or switch teams to view it."}
        </div>
      ) : null}

      <DeviceJsonDialog
        open={jsonOpen && !!device}
        device={device ?? null}
        onClose={() => setJsonOpen(false)}
      />
    </InShellPanel>
  );
}
